import { SUPABASE_URL } from '../utils/envVar';

export interface ThreatExplainNote {
  category: string;
  name: string;
  description: string;
  deprecated?: boolean;
  // Signal value the analyst attached to this reason (WHEEL-327), e.g. "3", and
  // the label describing what it measures, e.g. "Registration age (days)".
  // Both omitted when the note carries no value.
  value?: string;
  value_label?: string;
}

export interface ThreatExplainComment {
  text: string;
}

export interface ThreatExplainGsbLive {
  malicious: boolean;
  threat_types: string[];
}

export interface ThreatExplainGsbHistory {
  submitted_count: number;
  latest_threat_types: string[];
}

export interface ThreatExplainDnsRecord {
  record_type: string;
  address: string | null;
}

export interface ThreatExplainWhois {
  registrar: string | null;
  registration_date: string | null;
  expiration_date: string | null;
  registrant_country: string | null;
  name_servers: string[];
}

export interface ThreatExplainSsl {
  issuer: string | null;
  not_before: string | null;
  not_after: string | null;
}

export interface ThreatExplainPayload {
  domain: string;
  status: string;
  created_at: string;
  protected_asset_name: string | null;
  company_name: string | null;
  resurgent_threat: boolean | null;
  takedown_status: string | null;
  gsb_live: ThreatExplainGsbLive | null;
  gsb_history: ThreatExplainGsbHistory | null;
  dns_a_record: string | null;
  dns_records: ThreatExplainDnsRecord[];
  whois: ThreatExplainWhois | null;
  ssl: ThreatExplainSsl | null;
  notes: ThreatExplainNote[];
  comments: ThreatExplainComment[];
  /** Signed URL of the latest stored capture (WHEEL-394). The edge function
   * downloads it server side; it must still be valid when the summary is
   * generated, so the caller passes the freshest URL it has. */
  screenshot_url: string | null;
}

// Resolve selected note IDs against the notes catalog
// (useNotesValuesQuery from src/queries/noteComments.ts) into LLM-payload
// note objects.
export function mapSelectedNotes(
  selectedNoteIds: number[],
  notesCatalog: any[],
): ThreatExplainNote[] {
  const noteIndex = new Map<number, ThreatExplainNote>();
  for (const cat of notesCatalog ?? []) {
    for (const nv of cat?.note_values ?? []) {
      noteIndex.set(nv.id, {
        category: cat.name,
        name: nv.name,
        description: nv.description,
        deprecated: !!nv.deleted_at,
      });
    }
  }
  return selectedNoteIds
    .map(id => noteIndex.get(id))
    .filter(Boolean) as ThreatExplainNote[];
}

// Per-domain detection signals available at bulk-create time (sourced from FN
// candidates). No DNS/WHOIS/SSL/screenshot enrichment exists at this stage.
export interface BulkExplainDomainSignals {
  data_source?: string | null;
  detection_engine?: string | null;
  impersonation_probability?: number | null;
  impersonation_reasons?: string[];
  benign_reasons?: string[];
  html_title?: string | null;
  headings?: string[];
  copyright?: string | null;
  brand_similarity_score?: number | null;
  matched_token?: string | null;
  matched_token_type?: string | null;
  matched_favicon_url?: string | null;
  dns_active?: boolean | null;
  last_served_ip?: string | null;
}

export interface BulkExplainDomain {
  domain: string;
  protected_asset_name?: string | null;
  signals?: BulkExplainDomainSignals | null;
}

export interface BulkThreatExplainPayload {
  domains: BulkExplainDomain[];
  company_name?: string | null;
  protected_asset_name?: string | null;
  status?: string | null;
  analyst_context?: string | null;
  notes: ThreatExplainNote[];
}

export interface ThreatExplainSummary {
  model: string;
  explanation: string;
  tokens: { input: number; output: number };
  cost_usd: number;
}

export interface ThreatExplainResponse {
  prompt_version: string;
  generated_at: string;
  summary: ThreatExplainSummary;
}

async function callThreatExplain(body: object): Promise<ThreatExplainResponse> {
  const token = window.localStorage.getItem('userToken');
  if (!token) throw new Error('Missing userToken in localStorage');
  if (!SUPABASE_URL) throw new Error('Missing REACT_APP_SUPABASE_URL');

  const res = await fetch(
    `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/threat-explain`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );

  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(`threat-explain failed: ${res.status} ${errBody}`);
  }

  return res.json();
}

export function generateThreatExplanation(
  threat: ThreatExplainPayload,
): Promise<ThreatExplainResponse> {
  return callThreatExplain({ threat });
}

// One call covers the whole batch — the edge function generates one narrative
// for the batch, not one per domain.
export function generateBulkThreatExplanation(
  bulk: BulkThreatExplainPayload,
): Promise<ThreatExplainResponse> {
  return callThreatExplain({ bulk });
}
